/**
 * usePersona Hook
 *
 * Provides persona context to components. Reads persona from
 * localStorage (and eventually from user profile API).
 *
 * @module hooks/usePersona
 */
import {
  useState,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from 'react';
import {
  getPersonaConfig,
  PERSONA_CONFIGS,
} from '../config/personas';

const STORAGE_KEY = 'portfolistic_persona';

const PersonaContext = createContext(null);

export const PersonaProvider = ({ children }) => {
  const [persona, setPersonaState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored && PERSONA_CONFIGS[stored] ? stored : null;
    } catch {
      return null;
    }
  });
  const [isLoading] = useState(false);

  const personaConfig = useMemo(() => {
    return persona ? getPersonaConfig(persona) : null;
  }, [persona]);

  const switchPersona = useCallback((newPersona) => {
    if (PERSONA_CONFIGS[newPersona]) {
      setPersonaState(newPersona);
      try {
        localStorage.setItem(STORAGE_KEY, newPersona);
      } catch {
        /* ignore storage errors */
      }
    }
  }, []);

  const hasPersona = persona !== null;

  const value = useMemo(
    () => ({
      persona,
      personaConfig,
      switchPersona,
      hasPersona,
      isLoading,
    }),
    [persona, personaConfig, switchPersona, hasPersona, isLoading]
  );

  return (
    <PersonaContext.Provider value={value}>
      {children}
    </PersonaContext.Provider>
  );
};

const usePersona = () => {
  const context = useContext(PersonaContext);
  if (!context) {
    // Fallback for components outside provider (tests, etc.)
    return {
      persona: null,
      personaConfig: null,
      switchPersona: () => {},
      hasPersona: false,
      isLoading: false,
    };
  }
  return context;
};

export default usePersona;
